import type { PropsWithChildren } from 'react';

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  memo,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useReactive } from '@kizen/permissions/hooks/useReactive';
import {
  PermissionsContext,
  usePermissionsContext,
} from '@kizen/permissions/hooks/context';
import { useHasEdits } from '@kizen/permissions/hooks/useHasEdits';
import { useIsPermissionVisible } from '@kizen/permissions/hooks/useIsPermissionVisible';
import Button from '__components/Button';
import Loader from '__components/Kizen/Loader';
import { ModalHeader, ModalFooter } from '__components/Modals';
import ConfirmNavigationModal from '__components/Modals/presets/ConfirmNavigation';
import { toastVariant, useToast } from '__components/ToastProvider';
import { allClientFieldsQuery } from 'query/client';
import { allCustomObjectsQuery } from 'query/custom-object';
import {
  permissionGroupQuery,
  permissionGroupMetadataQuery,
  permissionGroupsQuery,
} from 'query/permission-group';
import TeamMemberService from 'services/TeamMemberService';
import { snakeToCamelCaseKeys } from '__services/helpers';
import { StyledModal, Content, StyledModalBody } from './styled';
import { usePreReleaseFeaturePermissionConfig } from './useHiddenPreReleasePermission';
import { AppAreaSection } from './AppAreaSection';
import { ContactsSection } from './ContactsSection';
import { CustomObjectSection } from './CustomObjectSection';
import { PermissionGroupName } from './components';
import { getOriginalError } from 'services/AxiosService';
import { REGISTERED_ROLE_AND_GROUP_NAME } from 'pages/Settings/helpers';
import ScrollBarDetached from '__components/Layout/ScrollBarDetached';
import { useSyncSizes } from '__components/Tables/Big/hooks';
import useSyncScroll from 'react-use-sync-scroll';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useSelector } from 'react-redux';
import { monitoringMessageHelper } from 'instrumentation/helpers';

type PermissionModalContentProps = {
  isEdit: boolean;
  nameError: string | null;
};

type PermissionModalProps = {
  id?: string;
  isLoading?: boolean;
  show?: boolean;
  onConfirm?(): void;
  onHide?(): void;
};

export const scrollOptions = {
  behavior: 'smooth',
  block: 'start',
};

const SectionVisibilityWrapper = (
  props: PropsWithChildren<{ permissionKey: string }>
) => {
  const { children, permissionKey } = props;
  const isVisible = useIsPermissionVisible(permissionKey);

  return isVisible ? children : null;
};

const PermissionModalContent = memo(
  forwardRef<HTMLDivElement, PermissionModalContentProps>((props, ref) => {
    const { isEdit, nameError } = props;

    const {
      meta_config: { sections, order },
      co_sections,
    } = usePermissionsContext();

    const sectionsMap = useMemo(() => {
      const map = sections.reduce((acc, section) => {
        acc.set(section.key, section);
        return acc;
      }, new Map());

      return map;
    }, [sections]);

    return (
      <Content ref={ref}>
        <div className="ContentForSyncHeight">
          <PermissionGroupName isEdit={isEdit} nameError={nameError} />
          {order.map((name) => {
            if (name === 'contacts_section') {
              return (
                <SectionVisibilityWrapper key={name} permissionKey={name}>
                  <ContactsSection />
                </SectionVisibilityWrapper>
              );
            }

            if (name === 'custom_object_entities') {
              return co_sections.map((co_section) => (
                <SectionVisibilityWrapper
                  key={co_section.key}
                  permissionKey={co_section.key}
                >
                  <CustomObjectSection
                    key={co_section.key}
                    section={co_section}
                  />
                </SectionVisibilityWrapper>
              ));
            }

            if (sectionsMap.has(name)) {
              return (
                <SectionVisibilityWrapper key={name} permissionKey={name}>
                  <AppAreaSection key={name} section={sectionsMap.get(name)} />
                </SectionVisibilityWrapper>
              );
            }

            monitoringMessageHelper(
              `Unexpected permission group metadata. Found section ${name} in 'order' without a config in 'sections'`
            );
          })}
        </div>
      </Content>
    );
  })
);

const PermissionModal = ({
  id,
  onConfirm,
  onHide,
  isLoading = false,
  show = true,
}: PermissionModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showToast] = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const { payload, name } = usePermissionsContext();
  const [nameError, setNameError] = useState<string | null>(null);
  const value = useReactive(name);
  const initialValue = useRef(value);
  const permissionNameInvalid = useReactive(
    name,
    (x) =>
      x?.length === 0 ||
      (x === REGISTERED_ROLE_AND_GROUP_NAME && !id) ||
      (x === REGISTERED_ROLE_AND_GROUP_NAME &&
        Boolean(id) &&
        initialValue.current !== REGISTERED_ROLE_AND_GROUP_NAME)
  );

  const hasEdits = useHasEdits();

  const handleHide = useCallback(() => {
    if (hasEdits) {
      setConfirmDiscard(true);
    } else {
      onHide?.();
    }
  }, [hasEdits, onHide]);

  const updateOrCreatePermissionGroup = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const updates = payload?.deref();
      if (updates) {
        if (id) {
          const updatedPermissionGroup =
            await TeamMemberService.updatePermissionGroup(id, updates);
          queryClient.setQueryData(
            permissionGroupQuery(id).queryKey,
            updatedPermissionGroup
          );
        } else {
          await TeamMemberService.createPermissionGroup(updates);
          queryClient.invalidateQueries({
            queryKey: permissionGroupsQuery().queryKey,
          });
        }
      }
      await onConfirm?.();
      onHide?.();
    } catch (error: any) {
      const { data, status } = error.response;
      // handle name error separately
      if (status === 400 && data?.errors?.name) {
        setNameError(data.errors.name);
        return;
      }
      const message = getOriginalError(error);
      const defaultMessage = t(
        'There was an error when trying to save. Please contact Kizen support for help with resolution.'
      );
      const errorMessage = message?.errors
        ? message.errors.details ||
          Object.values(message.errors)?.[0] ||
          defaultMessage
        : defaultMessage;

      showToast({
        variant: toastVariant.FAILURE,
        delay: 3000,
        message:
          typeof errorMessage === 'string' ? errorMessage : defaultMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [id, payload, onConfirm, onHide, showToast, queryClient, t]);

  // scrollbar
  const scrollbarContentWrapperRef = useRef();
  const [contentWrapperRefFn, contentWrapperRef] = useSyncSizes(
    scrollbarContentWrapperRef,
    '.ContentForSyncHeight',
    'height'
  );
  useSyncScroll(useRef([contentWrapperRef, scrollbarContentWrapperRef]), {
    vertical: true,
  });

  return (
    <StyledModal show={show} size="medium" onHide={handleHide}>
      <ModalHeader onClickClose={handleHide}>
        {id ? t('Edit Permission Group') : t('Add Permission Group')}
      </ModalHeader>
      <StyledModalBody isLoading={isLoading}>
        <Loader loading={isLoading}>
          <PermissionModalContent
            ref={contentWrapperRefFn}
            isEdit={
              Boolean(id) &&
              initialValue.current === REGISTERED_ROLE_AND_GROUP_NAME
            }
            nameError={nameError}
          />
        </Loader>
        <ScrollBarDetached
          ref={scrollbarContentWrapperRef}
          direction="vertical"
          scrollClassName="ContentForSyncHeight"
        />
      </StyledModalBody>
      <ModalFooter>
        <Button variant="text" color="blue" onClick={handleHide}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={updateOrCreatePermissionGroup}
          disabled={isLoading || isSubmitting || permissionNameInvalid}
          color="green"
        >
          {t('Save')}
        </Button>
        <ConfirmNavigationModal
          when={hasEdits}
          show={confirmDiscard}
          onHide={() => setConfirmDiscard(false)}
          onConfirm={onHide}
        />
      </ModalFooter>
    </StyledModal>
  );
};

export const PermissionGroupModal = ({
  id,
  onConfirm,
  onHide,
  show = true,
}: Omit<PermissionModalProps, 'isLoading'>) => {
  const { t } = useTranslation();

  const customObjectsQuery = useQuery(
    allCustomObjectsQuery({
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      select: (results) => {
        return results
          .filter((obj) => obj.name !== 'client_client')
          .toSorted((a, b) => a.object_name.localeCompare(b.object_name))
          .map(snakeToCamelCaseKeys);
      },
    })
  );

  const contactFieldsQuery = useQuery(
    allClientFieldsQuery({
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      select: (results) => {
        return results.map((field) => {
          const withAccess = {
            ...field,
            access: { ...field.access, edit: true, view: true },
          };
          return snakeToCamelCaseKeys(withAccess);
        });
      },
    })
  );

  const { data: permissionGroup, isLoading: pgLoading } = useQuery(
    permissionGroupQuery(id!, {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(id),
    })
  );

  const { data: permissionsMetadata, isLoading: metadataLoading } = useQuery(
    permissionGroupMetadataQuery({
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    })
  );

  const [initialStaleFlags] = useState({
    contactFields: contactFieldsQuery.isStale,
    customObjects: customObjectsQuery.isStale,
  });

  const contactObject = useSelector(getBusinessClientObject);

  const preReleaseFeaturesConfig = usePreReleaseFeaturePermissionConfig();
  const preReleaseConfig =
    pgLoading || !preReleaseFeaturesConfig
      ? preReleaseFeaturesConfig
      : {
          ...preReleaseFeaturesConfig,
          default:
            permissionGroup?.settings_section?.pre_release_features ??
            preReleaseFeaturesConfig.default,
        };
  const isLoading =
    pgLoading ||
    customObjectsQuery.isLoading ||
    contactFieldsQuery.isLoading ||
    metadataLoading;
  const isDataStale =
    (initialStaleFlags.customObjects &&
      !customObjectsQuery.isFetchedAfterMount) ||
    (initialStaleFlags.contactFields &&
      !contactFieldsQuery.isFetchedAfterMount);

  if (isLoading || isDataStale) {
    return <PermissionModal isLoading id={id} />;
  }

  return (
    <PermissionsContext
      translationFunc={t}
      permissionsConfig={permissionsMetadata!}
      preReleaseFeatureConfig={preReleaseConfig!}
      customObjects={customObjectsQuery.data}
      contactObjectId={contactObject.id}
      contactFields={contactFieldsQuery.data!}
      permissionGroup={permissionGroup}
    >
      <PermissionModal
        id={id}
        show={show}
        onConfirm={onConfirm}
        onHide={onHide}
      />
    </PermissionsContext>
  );
};
